import { getMeasurementSystem, getCurrency, getLocale } from '../config/settings';
import { FINANCIAL } from '../config/constants';

/**
 * Format currency using centralized settings
 */
export const formatCurrency = (amount: number): string => {
  const currency = getCurrency();
  const locale = getLocale();
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: FINANCIAL.CURRENCY_DECIMAL_PLACES,
    maximumFractionDigits: FINANCIAL.CURRENCY_DECIMAL_PLACES
  }).format(amount);
};

/**
 * Format distance using centralized measurement system
 */
export const formatDistance = (distanceInKm: number): string => {
  const system = getMeasurementSystem();
  
  if (system === 'metric') {
    if (distanceInKm < 1) {
      return `${Math.round(distanceInKm * 1000)}m`;
    }
    return `${distanceInKm.toFixed(1)}km`;
  } else {
    const miles = distanceInKm * 0.621371;
    if (miles < 1) {
      const feet = miles * 5280;
      return `${Math.round(feet)}ft`;
    }
    return `${miles.toFixed(1)}mi`;
  }
};

/**
 * Format weight using centralized measurement system
 */
export const formatWeight = (weightInKg: number): string => {
  const system = getMeasurementSystem();
  
  if (system === 'metric') {
    if (weightInKg < 1) {
      return `${Math.round(weightInKg * 1000)}g`;
    }
    return `${weightInKg.toFixed(1)}kg`;
  } else {
    const pounds = weightInKg * 2.20462;
    if (pounds < 1) {
      const ounces = pounds * 16;
      return `${Math.round(ounces)}oz`;
    }
    return `${pounds.toFixed(1)}lbs`;
  }
};

/**
 * Format temperature using centralized measurement system
 */
export const formatTemperature = (celsiusTemp: number): string => {
  const system = getMeasurementSystem();
  
  if (system === 'metric') {
    return `${Math.round(celsiusTemp)}°C`;
  } else {
    const fahrenheit = (celsiusTemp * 9/5) + 32;
    return `${Math.round(fahrenheit)}°F`;
  }
};

/**
 * Format numbers using centralized locale
 */
export const formatNumber = (num: number, options?: Intl.NumberFormatOptions): string => {
  const locale = getLocale();
  return new Intl.NumberFormat(locale, options).format(num);
};

/**
 * Format financial numbers (used across management accounts and admin components)
 * Shows empty string for zero, formatted number for positive, and parentheses for negative
 */
export const formatFinancialNumber = (value: number): string => {
  if (value === 0) return '';
  const formatted = Math.abs(value).toLocaleString();
  return value >= 0 ? formatted : `(${formatted})`;
};

/**
 * Get color classes for financial numbers (positive vs negative)
 */
export const getFinancialNumberColor = (value: number): string => {
  return value >= 0 ? 'text-emerald-600' : 'text-red-600';
};

/**
 * Format percentage using centralized locale
 */
export const formatPercentage = (num: number, decimals: number = 1): string => {
  const locale = getLocale();
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num / 100);
}; 